import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

export interface AutoFitTextProps {
  children: ReactNode;
  /** Ideal (largest) font size in px — the value renders here when it fits. */
  maxPx: number;
  /** Smallest font size in px it may shrink to before it's allowed to clip. Default 12. */
  minPx?: number;
  /** Applied to the (block) container that bounds the available width. */
  sx?: SxProps<Theme>;
}

/**
 * Renders a single line of text at `maxPx`, then shrinks the font just enough to
 * fit its container width — never wrapping, never overflowing. Used by the KPI
 * cards so long currency values (e.g. ₦24,706,500.00) stay inside the box at any
 * card width or magnitude, with full precision preserved (no abbreviation).
 *
 * The parent that provides the width MUST be able to shrink — give the flex
 * column that holds this `minWidth: 0` — otherwise the column grows to the text
 * and there is nothing to fit against.
 */
export function AutoFitText({ children, maxPx, minPx = 12, sx }: AutoFitTextProps) {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLSpanElement>(null);
  const [fontPx, setFontPx] = useState(maxPx);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;

    const fit = () => {
      const available = outer.clientWidth;
      if (available <= 0) return;
      let size = maxPx;
      inner.style.fontSize = `${size}px`;
      // Shrink 1px at a time until the text fits or we hit the floor.
      while (size > minPx && inner.scrollWidth > available) {
        size -= 1;
        inner.style.fontSize = `${size}px`;
      }
      setFontPx(size);
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(outer);
    return () => ro.disconnect();
  }, [children, maxPx, minPx]);

  return (
    <Box ref={outerRef} sx={{ width: '100%', minWidth: 0, overflow: 'hidden', ...sx }}>
      <Box
        component="span"
        ref={innerRef}
        sx={{ display: 'inline-block', whiteSpace: 'nowrap', fontSize: `${fontPx}px`, lineHeight: 'inherit' }}
      >
        {children}
      </Box>
    </Box>
  );
}
